import {
  Checkbox,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { createBatche, getNewBatcheID, updateBatch, addStudentsToBatch } from "../../API/Batches";
import { getCoachesList } from "../../API/coach-mgmt";
import { getCourses } from "../../API/courses";
import { getAllStudentProfiles } from "../../API/student-mgmt";
import { ERROR_TOAST, INFO_TOAST, SUCCESS_TOAST } from "../../helpers/toasters";
import { CourseData } from "../../pages/courses/CoursesList";
import * as XLSX from "xlsx";

import AddBatchStudentButton from "./addStudents/addStudentButton";
import SecondaryButton from "../buttons/SecondaryButton";
import "./createBatch.css";

type data = {
  _id: string;
  batch_id: String;
  name: string;
  course: CourseData[];
  active: boolean;
  coach: string;
  students: string[];
  days: number[];
  timeslot: string;
};

interface handelFuncProps {
  handelClose: () => void;
  data?: data;
}

export default function CreateBatch(props: any) {
  const [active, setOpen] = useState(false);
  const [batchSlot, setBatchSlot] = useState(
    props.data?.days_type || "Weekend"
  );
  const [selected, setSelected] = useState<string[]>(
    props.data?.students.map((m) => m._id) || []
  );
  const [batchType, setBatchType] = useState<any>(
    props.data?.type || "Evening"
  );
  const [days, setDays] = useState<string[]>(
    props.data?.days ?
      props.data.days.map(day => {
        const dayMap = {
          1: "Monday",
          2: "Tuesday",
          3: "Wednesday",
          4: "Thursday",
          5: "Friday",
          6: "Saturday",
          7: "Sunday"
        };
        return dayMap[day] || "";
      }).filter(Boolean) :
      ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]
  );
  const [isOpen, setIsOpen] = useState(false);
  const [CoachList, setCoachList] = useState<any>([]);
  const [Coach, setCoach] = useState<any>(props.data?.coach[0]._id || []);
  const [CourseList, setCourseList] = useState<any>([]);
  const [subCourseList, setSubCourseList] = useState<any>([]);
  const [Course, setCourse] = useState<any>(() => {
    // Extract ID properly from course object
    if (props.data?.course) {
      return typeof props.data.course === 'object' ? props.data.course._id : props.data.course;
    }
    return "";
  });
  const [name, setName] = useState<any>(props.data?.name || "");
  const [BatchId, setBatchId] = useState<any>(props.data?.batch_id || 0);
  const [timeSlot, setTimeSlot] = useState<any>(props.data?.timeslot || "");
  const [students, setStudents] = useState<string[]>(
    props.data?.students.map((m) => m._id) || []
  );

  const toggleDropdown = () => setIsOpen(!isOpen);

  useEffect(() => {
    if (props.data) {
      setCoach(props.data?.coach.map((m: any) => m._id));
      // Make sure we synch the students state with the selected state
      setSelected(props.data?.students.map((m) => m._id) || []);
      setStudents(props.data?.students.map((m) => m._id) || []);
    }

    getFirstdata();
  }, []);

  useEffect(() => {
    if (Course) {
      getNewBatcheID(Course)
        .then((data) => setBatchId(data))
        .catch((e) => ERROR_TOAST("Error in getting batch id"));
    }
  }, [Course]);

  useEffect(() => {
    getCoachesList()
      .then((data) => {
        setCoachList(data);
      })
      .catch((e) => {

      });

    getCourses()
      .then((data) => {
        if (!data) {
          ERROR_TOAST("Error Connecting to Server !");
        }
        // Directly set all active courses
        setSubCourseList(data.filter((m) => m.active));
      })
      .catch((e) => {

      });
  }, []);

  const DAYS_MAPPING = {
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
    Sunday: 7
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      if (!e.target?.result) {
        console.error("File read error: No data");
        return;
      }

      const arrayBuffer = e.target.result as ArrayBuffer;
      const data = new Uint8Array(arrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });

      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData: { [key: string]: any }[] = XLSX.utils.sheet_to_json(sheet);

      // Get all students with matching name or roll number
      try {
        // First, get all students
        const allStudents = await getAllStudentProfiles();
        if (!allStudents) {
          ERROR_TOAST("Error fetching student data");
          return;
        }

        // Match students from Excel with existing students by name or roll_no
        const matchedStudentIds: string[] = [];

        for (const excelStudent of jsonData) {
          const name = excelStudent.Name;
          const rollNo = excelStudent.roll_no?.toString();

          if (!name && !rollNo) continue;

          // Find matching student(s) in the database
          const matchedStudents = allStudents.filter(student =>
            (name && student.name?.toLowerCase() === name.toLowerCase()) ||
            (rollNo && student.roll_no?.toString() === rollNo)
          );

          // Add matched student IDs to our list
          matchedStudents.forEach(student => {
            if (student._id && !matchedStudentIds.includes(student._id)) {
              matchedStudentIds.push(student._id);
            }
          });
        }

        // Update both states with matched student IDs
        setStudents(matchedStudentIds);
        setSelected(matchedStudentIds);

        

        if (matchedStudentIds.length === 0) {
          INFO_TOAST("No matching students found in the system");
        } else {
          SUCCESS_TOAST(`Found ${matchedStudentIds.length} matching students`);
        }
      } catch (error) {
        console.error("Error processing student data:", error);
        ERROR_TOAST("Error processing student data");
      }
    };

    reader.readAsArrayBuffer(file);
  };


  const handleChange = (event: SelectChangeEvent<typeof Coach>) => {
    const {
      target: { value },
    } = event;
    setCoach(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleCourseChange = (e) => {
    const courseValue = e.target.value;
    

    // Handle different possible formats of course value
    let courseId;
    if (typeof courseValue === 'object' && courseValue !== null) {
      courseId = courseValue._id;
      
    } else {
      courseId = courseValue;
    }

    setCourse(courseId || "");
  };

  const submitData = async () => {
    
    if (!name.trim()) {
      return INFO_TOAST("Please enter a valid batch name!");
    }

    if (!BatchId) {
      return INFO_TOAST("Please fill all required fields !");
    }

    if (!Course) {
      return INFO_TOAST("Please choose a course !");
    }

    if (!Coach || Coach.length === 0) {
      return INFO_TOAST("Please choose a Coach !");
    }

    if (!timeSlot || !timeSlot.includes(' - ')) {
      return INFO_TOAST("Please select both start and end times!");
    }

    const [startTime, endTime] = timeSlot.split(' - ');
    if (startTime >= endTime) {
      return INFO_TOAST("Start time must be before end time!");
    }

    const data = {
      batch_id: BatchId,
      name,
      course: Course,
      type: batchType,
      active: true,
      coach: Coach,
      students,
      days: days.map(day => DAYS_MAPPING[day] ?? -1).filter(day => day !== -1),
      timeslot: timeSlot,
    };

    
    const result = await createBatche(data);
    

    if (result.message === "success") {
      SUCCESS_TOAST("Batch Created Successfully !");
      setTimeout(() => {
        handleClick();
      }, 2000);
    } else {
      return ERROR_TOAST("Please try again, Batch not created !");
    }
  };

  const getFirstdata = () => {
    getCoachesList()
      .then((data) => {
        setCoachList(data);
      })
      .catch((e) => {

      });

    getCourses()
      .then((data) => {
        if (!data) {
          ERROR_TOAST("Error Connecting to Server !");
        }
        setCourseList([...data.filter((m) => m.active)]);
      })
      .catch((e) => {

      });
  };

  const handleClick = () => {
    return props.handelClose();
  };

  const handleSubmit = () => {
    // Update both states to keep them in sync
    setStudents(selected);
    return;
  };

  const onSelect = (e) => {
    if (e.nativeEvent.target.checked) {
      const newSelected = [e.target.value, ...selected];
      setSelected(newSelected);
      setStudents(newSelected); // Keep students state in sync
    } else {
      const newSelected = [
        ...selected.slice(0, selected.indexOf(e.target.value)),
        ...selected.slice(
          selected.indexOf(e.target.value) + 1,
          selected.length
        ),
      ];
      setSelected(newSelected);
      setStudents(newSelected); // Keep students state in sync
    }
  };

  const downloadSampleSheet = () => {
    // Sample data showing the expected format
    const sampleData = [
      { Name: "John Doe", roll_no: "101" },
      { Name: "Emma Smith", roll_no: "102" },
      { Name: "Michael Johnson", roll_no: "103" },
      { Name: "Sophia Williams", roll_no: "104" }
    ];

    // Create a new workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");

    // Add headers explanation as a separate sheet
    const infoSheet = XLSX.utils.aoa_to_sheet([
      ["Instructions:"],
      ["1. Fill in student names in the 'Name' column and roll numbers in the 'roll_no' column"],
      ["2. You can provide either name or roll number or both for matching students"],
      ["3. The system will match students based on exact name or roll number"],
      ["4. Make sure the column headers remain exactly as 'Name' and 'roll_no'"],
      [""],
      ["Example format:"],
      ["Name", "roll_no"],
      ["John Doe", "101"],
      ["Emma Smith", "102"]
    ]);
    XLSX.utils.book_append_sheet(workbook, infoSheet, "Instructions");

    // Generate and download the file
    XLSX.writeFile(workbook, "Student_Batch_Upload_Template.xlsx");
  };

  const updateData = async () => {
    const data = {
      _id: props.data._id,
      name,
      batch_id: BatchId,
      course: Course,
      type: batchType,
      active: true,
      coach: Coach,
      days: days.map(day => DAYS_MAPPING[day]),
      timeslot: timeSlot,
      students, // This will now be properly synced with the selected state
    };

    

    const result = await updateBatch(data);

    if (!result) {
      return ERROR_TOAST("Something wrong happened");
    }

    if (result.message == "success") {
      SUCCESS_TOAST(`Batch (${result.data.batch_id}) Updated Successfully !`);
    }

    setTimeout(() => {
      handleClick();
    }, 2000);
  };

  return (
    <>
      <div className="addCourse">
        <div className="fullDesc">
          <div className="headerAC">
            <h2>{props.data ? "Update Batch" : "Create Batch"}</h2>
            <div
              className="top-svg"
              onClick={() => handleClick()}
              style={{ cursor: "pointer" }}
            >
              <svg
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 0C18.9782 0 22.7936 1.58035 25.6066 4.3934C28.4196 7.20644 30 11.0218 30 15C30 18.9782 28.4196 22.7936 25.6066 25.6066C22.7936 28.4196 18.9782 30 15 30C11.0218 30 7.20644 28.4196 4.3934 25.6066C1.58035 22.7936 0 18.9782 0 15C0 11.0218 1.58035 7.20644 4.3934 4.3934C7.20644 1.58035 11.0218 0 15 0ZM15 13.1807L11.5157 9.69643C11.2745 9.45518 10.9473 9.31964 10.6061 9.31964C10.2649 9.31964 9.93768 9.45518 9.69643 9.69643C9.45518 9.93768 9.31964 10.2649 9.31964 10.6061C9.31964 10.9473 9.45518 11.2745 9.69643 11.5157L13.1807 15L9.69643 18.4843C9.57697 18.6037 9.48221 18.7456 9.41757 18.9016C9.35292 19.0577 9.31964 19.225 9.31964 19.3939C9.31964 19.5629 9.35292 19.7301 9.41757 19.8862C9.48221 20.0423 9.57697 20.1841 9.69643 20.3036C9.81588 20.423 9.9577 20.5178 10.1138 20.5824C10.2699 20.6471 10.4371 20.6804 10.6061 20.6804C10.775 20.6804 10.9423 20.6471 11.0984 20.5824C11.2544 20.5178 11.3963 20.423 11.5157 20.3036L15 16.8193L18.4843 20.3036C18.6037 20.423 18.7456 20.5178 18.9016 20.5824C19.0577 20.6471 19.225 20.6804 19.3939 20.6804C19.5629 20.6804 19.7301 20.6471 19.8862 20.5824C20.0423 20.5178 20.1841 20.423 20.3036 20.3036C20.423 20.1841 20.5178 20.0423 20.5824 19.8862C20.6471 19.7301 20.6804 19.5629 20.6804 19.3939C20.6804 19.225 20.6471 19.0577 20.5824 18.9016C20.5178 18.7456 20.423 18.6037 20.3036 18.4843L16.8193 15L20.3036 11.5157C20.423 11.3963 20.5178 11.2544 20.5824 11.0984C20.6471 10.9423 20.6804 10.775 20.6804 10.6061C20.6804 10.4371 20.6471 10.2699 20.5824 10.1138C20.5178 9.9577 20.423 9.81588 20.3036 9.69643C20.1841 9.57697 20.0423 9.48221 19.8862 9.41757C19.7301 9.35292 19.5629 9.31964 19.3939 9.31964C19.225 9.31964 19.0577 9.35292 18.9016 9.41757C18.7456 9.48221 18.6037 9.57697 18.4843 9.69643L15 13.1807Z"
                  fill="#8E8EA1"
                />
              </svg>
            </div>
          </div>
          <div className="ac-border-top"></div>
          <div className="ad-body">
            <div className="previous-drop">
              <div className="dropdown">
                <label className="cat-hello" htmlFor="cname">
                  Batch Name*
                </label>
                <input
                  type="text"
                  name="name"
                  value={name}
                  placeholder="New Batch"
                  className="setSecond form-control dropdown-toggle"
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="dropdown">
                <label className="cat-hello" htmlFor="cname">
                  Course*
                </label>
                <select
                  className="form-select setFirst"
                  aria-label="Default select example"
                  name="Certification Levels"
                  onChange={(e) => handleCourseChange(e)}
                  value={Course}
                >
                  {!props.data && (
                    <option style={{ color: "#8e8ea1" }} value="">
                      Choose Course*
                    </option>
                  )}
                  {subCourseList.map((m: any) => (
                    <option key={m._id} value={m._id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="next">
            <div className="dropdown mx-5">
              <FormControl>
                <label className="cat-hello">Select Days*</label>
                <Select
                  multiple
                  value={days}
                  onChange={(event: SelectChangeEvent<string[]>) => {
                    const { value } = event.target;
                    setDays(typeof value === "string" ? value.split(",") : value);
                  }}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return "Select Days";
                    }
                    return selected.length > 2
                      ? `${selected.slice(0, 2).join(", ")}...`
                      : selected.join(", ");
                  }}
                >
                  {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => (
                    <MenuItem key={day} value={day}>
                      <Checkbox checked={days.includes(day)} />
                      <ListItemText primary={day} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="dropdown time-slot-container">
              <label className="cat-hello">Time Slot*</label>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="time"
                  className="form-control time-input"
                  value={timeSlot?.split(' - ')[0] || ''}
                  onChange={(e) => {
                    const startTime = e.target.value;
                    const endTime = timeSlot?.split(' - ')[1] || '';
                    setTimeSlot(`${startTime} - ${endTime}`);
                  }}
                />
                <span className="time-separator">to</span>
                <input
                  type="time"
                  className="form-control time-input"
                  value={timeSlot?.split(' - ')[1] || ''}
                  onChange={(e) => {
                    const endTime = e.target.value;
                    const startTime = timeSlot?.split(' - ')[0] || '';
                    setTimeSlot(`${startTime} - ${endTime}`);
                  }}
                />
              </div>
            </div>
            <div className="dropdown" style={{ marginRight: 50 }}>
              <FormControl sx={{ m: 0.5, width: 260 }}>
                <label className="cat-hello" htmlFor="cat">
                  Coach / Mentor*
                </label>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  // className="setFirst"
                  multiple
                  value={Coach}
                  onChange={handleChange}
                  input={<OutlinedInput label="Tag" />}
                  renderValue={(selected) => selected.length + " selected"}
                // MenuProps={MenuProps}
                >
                  {CoachList.map((m: any) => (
                    <MenuItem key={m._id} value={m._id}>
                      <Checkbox checked={Coach.indexOf(m._id) > -1} />
                      <ListItemText primary={m.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="dropdown">
              <div className="add-btn-batch">
                <AddBatchStudentButton
                  onSelect={onSelect}
                  handleSubmit={handleSubmit}
                  rows={students}
                />
              </div>
              <div className="file-upload-section">
                <div className="file-upload-row">
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    onChange={handleFileUpload}
                    className="file-input"
                  />
                  <SecondaryButton 
                    onClick={downloadSampleSheet}
                    className="sample-button"
                  >
                    Download Sample
                  </SecondaryButton>
                </div>
                <div className="selected-batch-students">
                  <p>
                    <b>{students.length}</b> Students
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }} >
            <div className="down-buttons" style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                className="create-button"
                onClick={() => {
                  
                  props.data ? updateData() : submitData();
                }}
              >
                {props.data ? "Update Batch" : "Create Batch"}
              </button>
              <button className="cancel-button" onClick={() => handleClick()}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div >
    </>
  );
}